/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  Card,
  HelperText,
  Text,
} from '@sonarsource/echoes-react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { IntegrationConfiguration } from '~sq-server-commons/types/integrations';
import { SlackIntegrationConfigurationDeletion } from './SlackIntegrationConfigurationDeletion';
import { SlackIntegrationConfigurationUpdate } from './SlackIntegrationConfigurationUpdate';
import { useSlackAppDirectInstallUrl } from './hooks';

interface SlackIntegrationConfiguredProps {
  className?: string;
  slackConfiguration: IntegrationConfiguration;
}

export function SlackIntegrationConfigured({
  className,
  slackConfiguration,
}: Readonly<SlackIntegrationConfiguredProps>) {
  const {
    isLoading: isLoadingSlackAppDirectInstallUrl,
    rotateOauthState,
    slackAppDirectInstallUrl,
  } = useSlackAppDirectInstallUrl();

  return (
    <div className={classNames('sw-flex sw-flex-col sw-gap-6', className)}>
      <Card>
        <Card.Header
          hasDivider
          rightContent={
            <ButtonGroup>
              <SlackIntegrationConfigurationUpdate slackConfiguration={slackConfiguration} />
              <SlackIntegrationConfigurationDeletion slackConfigurationId={slackConfiguration.id} />
            </ButtonGroup>
          }
          title={<FormattedMessage id="settings.slack.configuration.header" />}
        />

        <Card.Body>
          <div className="sw-flex sw-gap-9">
            <div className="sw-flex sw-flex-col sw-gap-3">
              <Text isHighlighted>
                <FormattedMessage id="settings.slack.configuration.client_id" />
              </Text>
              <Text isHighlighted>
                <FormattedMessage id="settings.slack.configuration.client_secret" />
              </Text>
              <Text isHighlighted>
                <FormattedMessage id="settings.slack.configuration.signing_secret" />
              </Text>
            </div>
            <div className="sw-flex sw-flex-col sw-gap-3">
              <Text className="fs-mask">{slackConfiguration.clientId}</Text>
              <SecretFieldValuePlaceholder />
              <SecretFieldValuePlaceholder />
            </div>
          </div>
        </Card.Body>
      </Card>

      <div className="sw-flex sw-items-center sw-gap-2">
        <Button
          enableOpenInNewTab
          isDisabled={isLoadingSlackAppDirectInstallUrl || slackAppDirectInstallUrl === undefined}
          isLoading={isLoadingSlackAppDirectInstallUrl}
          onClick={rotateOauthState}
          to={slackAppDirectInstallUrl ?? ''}
          variety={ButtonVariety.Default}
        >
          <FormattedMessage id="settings.slack.install_app.label" />
        </Button>
        <HelperText>
          <FormattedMessage id="settings.slack.install_app.hint" />
        </HelperText>
      </div>
    </div>
  );
}

function SecretFieldValuePlaceholder() {
  return (
    <Text isSubtle>
      <FormattedMessage id="hidden_for_security" />
    </Text>
  );
}
